import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IShelterKioskReason from '../../interfaces/IShelterKioskReason'
import {
  SearchRequest,
  ShelterKioskReasonCreateRequest,
  ShelterKioskReasonUpdateRequest,
  ShelterKioskReasonDeleteRequest,
  ShelterKioskReasonUpdateQuestionRequest,
  ShelterKioskReasonTranslationSaveRequest,
  ShelterKioskReasonTranslationDeleteRequest,
  ShelterKioskReasonSmartResponseRequest
} from '../../services/api/interfaces/ApiRequest'
import {
  shelterKioskReasonList,
  shelterKioskReasonCreate,
  shelterKioskReasonUpdate,
  shelterKioskReasonDelete,
  shelterKioskReasonUpdateQuestion,
  shelterKioskReasonTranslationSave,
  shelterKioskReasonTranslationDelete,
  shelterKioskReasonSmartResponseGet,
  shelterKioskReasonSmartResponseSave,
} from '../../services/api'
import IShelterSmartResponse from '../../interfaces/IShelterSmartResponse'

interface ShelterKioskReasonContextState extends BaseContextState {
  shelterKioskReasons: Array<IShelterKioskReason> | null
  smartResponse: IShelterSmartResponse | null
}

export interface ShelterKioskReasonContextData extends ShelterKioskReasonContextState {
  resetErrors: Function,

  requestShelterKioskReasonList: (args: SearchRequest) => Promise<any>,
  requestShelterKioskReasonCreate: (args: ShelterKioskReasonCreateRequest) => Promise<any>
  requestShelterKioskReasonUpdate: (args: ShelterKioskReasonUpdateRequest) => Promise<any>
  requestShelterKioskReasonDelete: (args: ShelterKioskReasonDeleteRequest) => Promise<any>
  requestShelterKioskReasonUpdateQuestion: (args: ShelterKioskReasonUpdateQuestionRequest) => Promise<any>
  requestShelterKioskReasonTranslationSave: (args: ShelterKioskReasonTranslationSaveRequest) => Promise<any>
  requestShelterKioskReasonTranslationDelete: (args: ShelterKioskReasonTranslationDeleteRequest) => Promise<any>
  requestShelterKioskReasonSmartResponseGet: (args: ShelterKioskReasonSmartResponseRequest) => Promise<any>
  requestShelterKioskReasonSmartResponseSave: (args: ShelterKioskReasonSmartResponseRequest) => Promise<any>
}

// Create context
const { Provider, Consumer } = createContext({} as ShelterKioskReasonContextData)

// Export consumer
export const ShelterKioskReasonConsumer = Consumer

export class ShelterKioskReasonProvider extends BaseContextProvider {
  state: ShelterKioskReasonContextState = {
    shelterKioskReasons: null,
    smartResponse: null
  }

  lastListPayload: SearchRequest = {}

  /**
   * Make api request for kiosk list
   * 
   * @param {SearchRequest} payload 
   */
  requestShelterKioskReasonList = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        this.lastListPayload = payload
        const response = await shelterKioskReasonList(payload)
  
        // Process our response
        this.processResponse(response, { 
          paginate: {
            destination: 'shelterKioskReasons'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for shelter kiosk reason create
   * 
   * @param {ShelterKioskReasonCreateRequest} payload 
   */
  requestShelterKioskReasonCreate = (payload: ShelterKioskReasonCreateRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await shelterKioskReasonCreate(payload)
        this.processResponse(response, {
          keepFetching: true
        })

        if (this.state.success) {
          await this.requestShelterKioskReasonList(this.lastListPayload)
          resolve(true)
        } else {
          this.setState({
            fetching: false
          }, () => {
            reject(this.state.error)
          })
        }
      })
    })
  }

  /**
   * Make api request for shelter kiosk reason update
   * 
   * @param {ShelterKioskReasonUpdateRequest} payload 
   */
   requestShelterKioskReasonUpdate = (payload: ShelterKioskReasonUpdateRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await shelterKioskReasonUpdate(payload)
        this.processResponse(response, {
          keepFetching: true
        })

        if (this.state.success) {
          await this.requestShelterKioskReasonList(this.lastListPayload)
          resolve(true)
        } else {
          this.setState({
            fetching: false
          }, () => {
            reject(this.state.error)
          })
        }
      })
    })
  }

  /**
   * Make api request for shelter kiosk reason delete
   * 
   * @param {ShelterKioskReasonDeleteRequest} payload 
   */
   requestShelterKioskReasonDelete = (payload: ShelterKioskReasonDeleteRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await shelterKioskReasonDelete(payload)
        this.processResponse(response, {
          keepFetching: true
        })

        if (this.state.success) {
          await this.requestShelterKioskReasonList(this.lastListPayload)
          resolve(true)
        } else {
          this.setState({
            fetching: false
          }, () => {
            reject(this.state.error)
          })
        }
      })
    })
  }

  /**
   * Make api request for shelter kiosk reason update question
   * 
   * @param {ShelterKioskReasonUpdateQuestionRequest} payload 
   */
   requestShelterKioskReasonUpdateQuestion = (payload: ShelterKioskReasonUpdateQuestionRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await shelterKioskReasonUpdateQuestion(payload)
        this.processResponse(response, {
          keepFetching: true
        })

        if (this.state.success) {
          await this.requestShelterKioskReasonList(this.lastListPayload)
          resolve(true)
        } else {
          this.setState({
            fetching: false
          }, () => {
            reject(this.state.error)
          })
        }
      })
    })
  }

  /**
   * Make api request for shelter kiosk reason translation save
   * 
   * @param {ShelterKioskReasonTranslationSaveRequest} payload 
   */
   requestShelterKioskReasonTranslationSave = (payload: ShelterKioskReasonTranslationSaveRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await shelterKioskReasonTranslationSave(payload)
        this.processResponse(response, {
          keepFetching: true
        })

        if (this.state.success) {
          await this.requestShelterKioskReasonList(this.lastListPayload)
          resolve(true)
        } else {
          this.setState({
            fetching: false
          }, () => {
            reject(this.state.error)
          })
        }
      })
    })
  }

  /**
   * Make api request for shelter kiosk reason translation delete
   * 
   * @param {ShelterKioskReasonTranslationDeleteRequest} payload 
   */
   requestShelterKioskReasonTranslationDelete = (payload: ShelterKioskReasonTranslationDeleteRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await shelterKioskReasonTranslationDelete(payload)
        this.processResponse(response, {
          keepFetching: true
        })

        if (this.state.success) {
          await this.requestShelterKioskReasonList(this.lastListPayload)
          resolve(true)
        } else {
          this.setState({
            fetching: false
          }, () => {
            reject(this.state.error)
          })
        }
      })
    })
  }

  /**
   * Make api request for shelter kiosk reason smart response get
   * 
   * @param {ShelterKioskReasonSmartResponseRequest} payload 
   */
   requestShelterKioskReasonSmartResponseGet = (payload: ShelterKioskReasonSmartResponseRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await shelterKioskReasonSmartResponseGet(payload)
        this.processResponse(response, {
          mappings: {
            origin: 'smart_response', destination: 'smartResponse'
          }
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for shelter kiosk reason smart response save
   * 
   * @param {ShelterKioskReasonSmartResponseRequest} payload 
   */
   requestShelterKioskReasonSmartResponseSave = (payload: ShelterKioskReasonSmartResponseRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await shelterKioskReasonSmartResponseSave(payload)
        this.processResponse(response, {
          mappings: {
            origin: 'smart_response', destination: 'smartResponse'
          }
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestShelterKioskReasonList: this.requestShelterKioskReasonList,
          requestShelterKioskReasonCreate: this.requestShelterKioskReasonCreate,
          requestShelterKioskReasonUpdate: this.requestShelterKioskReasonUpdate,
          requestShelterKioskReasonDelete: this.requestShelterKioskReasonDelete,
          requestShelterKioskReasonUpdateQuestion: this.requestShelterKioskReasonUpdateQuestion,
          requestShelterKioskReasonTranslationSave: this.requestShelterKioskReasonTranslationSave,
          requestShelterKioskReasonTranslationDelete: this.requestShelterKioskReasonTranslationDelete,
          requestShelterKioskReasonSmartResponseGet: this.requestShelterKioskReasonSmartResponseGet,
          requestShelterKioskReasonSmartResponseSave: this.requestShelterKioskReasonSmartResponseSave
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
